import React from 'react'

import { useModelState } from '../../model-hooks/use-model-state'
import ChartLayout from '../../models/chart_layout'
import { getUuid } from '../../utils/helpers'
import { compareHlc, incrementHlc, initHlc, receiveHlc, serializeHlc } from '../../utils/hlc'
import { AutoSaveElement, DRAWINGS_SAVE_INTERVAL_MS, DRAWINGS_UPDATE_INTERVAL_MS } from './constants'
import { useDrawingAutoSaveApi } from './use-drawing-autosave-api'
import { useIsAutoSaveActive } from './use-is-auto-save-active'
import { getTickersAndIndicatorsInLayoutModel, mergeUpdatedDrawingsToInternalStore } from './utils'

function getNoteElementId(ticker: string) {
  return `note-${ticker}`
}

export function useAutoSaveNote(chartLayout: ChartLayout) {
  const { fetchDrawings, saveDrawings } = useDrawingAutoSaveApi()
  const isAutoSaveActive = useIsAutoSaveActive(chartLayout)
  const activeChart = useModelState(chartLayout.activeChart, {
    watchProperties: ['quote'],
  })
  const activeTicker = activeChart?.quote()?.ticker

  const [activeNote, setActiveNote] = React.useState<string>('')
  const internalStoreRef = React.useRef<AutoSaveElement[]>([])
  const instanceUUIDRef = React.useRef(getUuid())
  const latestRemoteUpdateTimestampRef = React.useRef(initHlc(instanceUUIDRef.current, 0))
  const lastestLocalUpdateTimestampRef = React.useRef(initHlc(instanceUUIDRef.current, 0))
  const saveTimeoutRef = React.useRef<NodeJS.Timeout>()

  const updateActiveNote = React.useCallback(() => {
    const noteElement = internalStoreRef.current
      .filter(({ containerType, ticker }) => containerType === 'note' && ticker === activeTicker)
      .sort((a, b) => compareHlc(b.lastChange, a.lastChange))[0]

    let newNote = ''
    if (noteElement) {
      newNote = JSON.parse(noteElement.elementAttrs)?.text ?? ''
    }

    setActiveNote(newNote)
  }, [activeTicker])

  const saveLatestChanges = React.useCallback(
    (withDelayInMs = DRAWINGS_SAVE_INTERVAL_MS) => {
      updateActiveNote()
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(async () => {
        const result = await saveDrawings(
          internalStoreRef.current.filter(
            (item) =>
              compareHlc(item.lastChange, lastestLocalUpdateTimestampRef.current) > 0 &&
              item.lastChange.uuid === instanceUUIDRef.current
          )
        )
        if (result) {
          lastestLocalUpdateTimestampRef.current = receiveHlc(lastestLocalUpdateTimestampRef.current, result)
        }
      }, withDelayInMs)
    },
    [saveDrawings, updateActiveNote]
  )

  const onNoteChange = React.useCallback(
    (note: string) => {
      if (!activeTicker) {
        return
      }

      const newNoteElement: AutoSaveElement = {
        elementId: getNoteElementId(activeTicker),
        zIndex: 0,
        ticker: activeTicker,
        lastChange: incrementHlc(lastestLocalUpdateTimestampRef.current, Date.now()),
        changeType: 'update',
        containerType: 'note',
        elementAttrs: JSON.stringify({ text: note }),
      }

      internalStoreRef.current = [
        ...internalStoreRef.current.filter(
          ({ ticker, containerType }) => containerType !== 'note' && ticker !== activeTicker
        ),
        newNoteElement,
      ]
      saveLatestChanges()
    },
    [saveLatestChanges, activeTicker]
  )

  const checkNotesUpdate = React.useCallback(
    async (isRefetchAll = false) => {
      const { tickers } = getTickersAndIndicatorsInLayoutModel(chartLayout)
      if (isRefetchAll) {
        latestRemoteUpdateTimestampRef.current = initHlc(instanceUUIDRef.current, 0)
      }

      const updatedDrawings = await fetchDrawings({
        tickers,
        containerTypes: ['note'],
        lastChange: serializeHlc(latestRemoteUpdateTimestampRef.current),
      })

      const { newLastUpdateTimestamp, newInternalStore } = mergeUpdatedDrawingsToInternalStore({
        updatedDrawings,
        currentInternalStore: internalStoreRef.current,
        lastUpdateTimestamp: latestRemoteUpdateTimestampRef.current,
      })

      internalStoreRef.current = newInternalStore
      latestRemoteUpdateTimestampRef.current = newLastUpdateTimestamp
      if (isRefetchAll) {
        lastestLocalUpdateTimestampRef.current = newLastUpdateTimestamp
      }

      const currentNoteElement = updatedDrawings.find(
        ({ ticker, elementId }) => ticker === activeTicker && elementId === getNoteElementId(activeTicker)
      )
      const currentNoteText = chartLayout.idea?.note

      if (currentNoteElement && isRefetchAll && currentNoteText) {
        const currentNoteElementText = JSON.parse(currentNoteElement.elementAttrs)?.text ?? ''
        const appendSavedNote = currentNoteElementText ? `\r\n${currentNoteElementText}` : ''
        onNoteChange(`${currentNoteText}${appendSavedNote}`)
      } else {
        updateActiveNote()
      }
    },
    [chartLayout, activeTicker, fetchDrawings, onNoteChange, updateActiveNote]
  )

  React.useEffect(() => {
    if (!isAutoSaveActive) {
      return
    }

    updateActiveNote()
    checkNotesUpdate(true)
    const checkDrawingUpdateInterval = setInterval(checkNotesUpdate, DRAWINGS_UPDATE_INTERVAL_MS)

    return () => {
      clearInterval(checkDrawingUpdateInterval)
    }
  }, [chartLayout, internalStoreRef, checkNotesUpdate, isAutoSaveActive, updateActiveNote])

  React.useEffect(
    () => () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    },
    []
  )

  return {
    onNoteChange,
    activeNote,
    isAutoSaveActive,
  }
}
